import * as React from 'react';
import { EyeLine, EyeCloseLine, ShieldLine, SafeAlertLine } from '@mingcute/react';

import { cn } from '../lib/cn';

export interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4;
  hasLength: boolean;
  hasMixedCase: boolean;
  hasNumber: boolean;
  hasSymbol: boolean;
}

export function evaluatePassword(pw: string): PasswordStrength {
  const hasLength = pw.length >= 12;
  const hasMixedCase = /[a-z]/.test(pw) && /[A-Z]/.test(pw);
  const hasNumber = /\d/.test(pw);
  const hasSymbol = /[^A-Za-z0-9]/.test(pw);
  const score = [hasLength, hasMixedCase, hasNumber, hasSymbol].filter(Boolean).length as 0 | 1 | 2 | 3 | 4;
  return { score, hasLength, hasMixedCase, hasNumber, hasSymbol };
}

const strengthLabel = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'];
const strengthColor = [
  'bg-[var(--color-danger)]',
  'bg-[var(--color-danger)]',
  'bg-[var(--color-warning)]',
  'bg-[var(--color-info)]',
  'bg-[var(--color-success)]',
];

export interface PasswordInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  invalid?: boolean;
  showStrength?: boolean;
  hibpStatus?: 'unknown' | 'checking' | 'safe' | 'pwned';
}

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, invalid, showStrength, hibpStatus, value, disabled, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);
    const strength = React.useMemo(
      () => evaluatePassword(typeof value === 'string' ? value : ''),
      [value],
    );

    return (
      <div className="flex flex-col gap-1.5">
        <div
          className={cn(
            'flex h-9 items-center gap-2 rounded-[var(--radius-md)] border bg-[var(--color-surface)] px-3',
            'focus-within:ring-2 focus-within:ring-[var(--color-ring)]',
            invalid ? 'border-[var(--color-danger)]' : 'border-[var(--color-border)]',
            disabled && 'cursor-not-allowed opacity-60',
            className,
          )}
        >
          <input
            ref={ref}
            type={visible ? 'text' : 'password'}
            value={value}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            className="h-full flex-1 bg-transparent text-sm text-[var(--color-fg)] outline-none placeholder:text-[var(--color-fg-muted)]"
            {...props}
          />
          {hibpStatus && hibpStatus !== 'unknown' ? (
            <span aria-label={`HIBP status: ${hibpStatus}`} className="shrink-0">
              {hibpStatus === 'safe' ? (
                <ShieldLine size={16} className="text-[var(--color-success)]" />
              ) : hibpStatus === 'pwned' ? (
                <SafeAlertLine size={16} className="text-[var(--color-danger)]" />
              ) : (
                <ShieldLine size={16} className="animate-pulse text-[var(--color-fg-muted)]" />
              )}
            </span>
          ) : null}
          <button
            type="button"
            tabIndex={-1}
            aria-label={visible ? 'Hide password' : 'Show password'}
            disabled={disabled}
            onClick={() => setVisible((v) => !v)}
            className="shrink-0 text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]"
          >
            {visible ? <EyeCloseLine size={16} /> : <EyeLine size={16} />}
          </button>
        </div>
        {showStrength && typeof value === 'string' && value.length > 0 ? (
          <div className="flex flex-col gap-1">
            <div className="flex gap-1" aria-hidden>
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={cn(
                    'h-1 flex-1 rounded-full',
                    i < strength.score ? strengthColor[strength.score] : 'bg-[var(--color-surface-2)]',
                  )}
                />
              ))}
            </div>
            <p className="text-[10px] text-[var(--color-fg-muted)]">
              {strengthLabel[strength.score]} • need 12+ chars, mixed case, number & symbol
            </p>
          </div>
        ) : null}
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';
